import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Calendar, 
  Clock, 
  Plus, 
  Settings, 
  AlertCircle, 
  CheckCircle,
  X,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addWeeks, addMonths } from 'date-fns';
import { formatUTCToISTTime, getCurrentIST } from '../../../../utils/timezone';
import { useAvailability } from '../../hooks';
import { CARD_STYLES, BUTTON_STYLES, INPUT_STYLES } from '../../../receptionist-dashboard/utils/constants';

const formatHHMMTo12Hour = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return '';
  const match = hhmm.trim().match(/^(\d{1,2})(?::\s*(\d{1,2}))?(?:\s*(AM|PM))?$/i);
  if (!match) return '';
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2] ?? '0', 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';
  const hasMeridiem = Boolean(match[3]);
  const period = hasMeridiem
    ? match[3].toUpperCase()
    : hours >= 12
      ? 'PM'
      : 'AM';
  const normalizedHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${normalizedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Time selector: separate time text (hh:mm) and AM/PM dropdown; emits value as 'HH:mm'
const TimeSelector = ({ value, onChange, isToday, minTime, error, autoFillCurrent = false }) => {
  const [timeStr, setTimeStr] = useState('');
  const [period, setPeriod] = useState('AM');
  const [localError, setLocalError] = useState('');
  const [hasAutoFilled, setHasAutoFilled] = useState(false);

  const pad2 = (n) => n.toString().padStart(2, '0');

  const fromHHMM = useCallback((hhmm) => {
    if (!hhmm) {
      setTimeStr('');
      setPeriod('AM');
      return;
    }
    const [hStr, mStr] = hhmm.split(':');
    const h24 = parseInt(hStr, 10);
    const m = parseInt(mStr, 10) || 0;
    const p = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    setTimeStr(`${h12}:${pad2(m)}`);
    setPeriod(p);
  }, []);

  useEffect(() => {
    fromHHMM(value);
    // Reset hasAutoFilled when value changes externally (e.g., modal reopens or date changes)
    if (!value) {
      setHasAutoFilled(false);
    }
  }, [value, fromHHMM]);

  const toHHMM = (tStr, p) => {
    if (!tStr) return '';
    const match = tStr.match(/^(\d{1,2})(?::(\d{2}))?$/);
    if (!match) return null;
    let h = parseInt(match[1], 10);
    let m = match[2] ? parseInt(match[2], 10) : 0;
    if (isNaN(h) || isNaN(m) || h < 1 || h > 12 || m < 0 || m > 59) return null;
    if (p === 'AM') {
      h = h % 12; // 12 AM -> 0
    } else {
      h = (h % 12) + 12; // 12 PM -> 12, 1 PM -> 13, ..., 11 PM -> 23
    }
    return `${pad2(h)}:${pad2(m)}`;
  };

  const commit = (nextTimeStr = timeStr, nextPeriod = period) => {
    setLocalError('');
    const hhmm = toHHMM(nextTimeStr, nextPeriod);
    if (hhmm === '') {
      onChange && onChange('');
      return;
    }
    if (!hhmm) {
      setLocalError('Enter time as h:mm');
      return;
    }
    if (isToday && minTime && hhmm < minTime) {
      // Try flipping AM/PM automatically if that would satisfy the constraint
      const flippedPeriod = nextPeriod === 'AM' ? 'PM' : 'AM';
      const flipped = toHHMM(nextTimeStr, flippedPeriod);
      if (flipped && flipped >= minTime) {
        setPeriod(flippedPeriod);
        onChange && onChange(flipped);
        return;
      }
      // Snap to minimum allowed time for a smoother UX
      fromHHMM(minTime);
      onChange && onChange(minTime);
      return;
    }
    onChange && onChange(hhmm);
  };

  const hasError = Boolean(error || localError);
  const currentValue = timeStr ? toHHMM(timeStr, period) : '';
  const prettyValue = currentValue ? formatHHMMTo12Hour(currentValue) : '';

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <input
            type="text"
            inputMode="numeric"
            placeholder="h:mm"
            value={timeStr}
            onFocus={(e) => {
              // Auto-fill current time when clicking on empty field (only for start time)
              if (autoFillCurrent && !value && !hasAutoFilled) {
                const currentIST = getCurrentIST();
                const hours = currentIST.getHours();
                const minutes = currentIST.getMinutes();
                const p = hours >= 12 ? 'PM' : 'AM';
                const h12 = hours % 12 === 0 ? 12 : hours % 12;
                const timeString = `${h12}:${pad2(minutes)}`;
                setTimeStr(timeString);
                setPeriod(p);
                setHasAutoFilled(true);
                // Commit the time immediately
                const hhmm = toHHMM(timeString, p);
                if (hhmm) {
                  onChange && onChange(hhmm);
                }
              }
            }}
            onChange={(e) => {
              // Allow only digits and ':' with strict format: h:mm or hh:mm (max 2 digits before colon, exactly 2 after)
              const raw = e.target.value.replace(/[^0-9:]/g, '');
              
              // Prevent multiple colons
              const colonCount = (raw.match(/:/g) || []).length;
              if (colonCount > 1) return;
              
              const parts = raw.split(':');
              let formatted = raw;
              
              if (parts.length === 1) {
                // Only hours entered, limit to 2 digits
                const digits = parts[0].slice(0, 2);
                formatted = digits;
                // Auto-insert colon after 2 digits
                if (digits.length === 2) {
                  formatted = digits + ':';
                }
              } else if (parts.length === 2) {
                // Hours and minutes, limit hours to 2 digits and minutes to 2 digits
                const hours = parts[0].slice(0, 2);
                const minutes = parts[1].slice(0, 2);
                formatted = hours + ':' + minutes;
              }
              
              setTimeStr(formatted);
              if (localError) setLocalError('');
            }}
            onBlur={() => commit()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              }
            }}
            className={`w-full px-3 py-2 rounded-lg border outline-none transition-colors focus:ring-2 focus:ring-[#2D869C]/30 focus:border-[#2D869C] ${
              hasError ? 'border-[#E99797]' : 'border-[#9DC9D4]/40'
            }`}
          />
        </div>
        <div
          className={`inline-flex rounded-lg border overflow-hidden ${
            hasError ? 'border-[#E99797]' : 'border-[#9DC9D4]/40'
          }`}
          role="group"
          aria-label="Select time period"
        >
          <button
            type="button"
            onClick={() => {
              setPeriod('AM');
              commit(timeStr, 'AM');
            }}
            className={`px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D869C]/30 ${
              period === 'AM'
                ? 'bg-[#2D869C] text-white'
                : 'bg-white text-[#838383] hover:bg-[#9DC9D4]/10'
            }`}
            aria-pressed={period === 'AM'}
          >
            AM
          </button>
          <button
            type="button"
            onClick={() => {
              setPeriod('PM');
              commit(timeStr, 'PM');
            }}
            className={`px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2D869C]/30 ${
              period === 'PM'
                ? 'bg-[#2D869C] text-white'
                : 'bg-white text-[#838383] hover:bg-[#9DC9D4]/10'
            }`}
            aria-pressed={period === 'PM'}
          >
            PM
          </button>
        </div>
      </div>
      
      {(error || localError) && (
        <p className="mt-2 text-sm text-red-600 font-semibold">{localError || error}</p>
      )}
      {isToday && !error && !localError && (
        <p className="mt-2 text-sm font-medium text-gray-700">Cannot select time in past</p>
      )}
    </div>
  );
};

// Custom Date Picker Component
const CustomDatePicker = ({ value, onChange, minDate, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => value ? new Date(value) : new Date());

  useEffect(() => {
    if (value && !Number.isNaN(new Date(value).getTime())) {
      setViewDate(new Date(value));
    }
  }, [value]);

  const toggleCalendar = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleSelect = (date) => {
    onChange(format(date, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const handleMonthChange = (amount) => {
    setViewDate(prev => addMonths(prev, amount));
  };

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  const displayValue = value ? format(new Date(value), 'EEEE, MMMM d, yyyy') : '';

  return (
    <div className="relative">
       <button 
         type="button"
         onClick={toggleCalendar}
         className={`w-full border-2 rounded-lg px-4 py-2.5 text-md font-medium text-left bg-white flex items-center justify-between transition-all ${
            error ? 'border-red-300' : isOpen ? 'border-[#2D869C] ring-2 ring-[#9DC9D4]' : 'border-gray-200 hover:border-[#2D869C]'
         }`}
       >
         <div className="flex items-center gap-3">
            <span className={value ? "text-gray-900" : "text-gray-400"}>
              {value ? displayValue : "Select Date"}
            </span>
         </div>
         <Calendar className="w-5 h-5 text-gray-500" />
       </button>
       
       {error && <p className="text-red-600 text-sm mt-1 font-semibold">{error}</p>}

       {isOpen && (
         <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="absolute top-12 left-0 z-50 bg-white rounded-xl border-2 border-gray-200 shadow-xl w-[320px] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
               <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] p-4 flex items-center justify-between text-white">
                  <button type="button" onClick={(e) => { e.stopPropagation(); handleMonthChange(-1); }} className="p-1 hover:bg-white/20 rounded transition-colors">
                    <ChevronLeft/>
                  </button>
                  <span className="font-bold text-lg font-sans">{format(viewDate, 'MMMM yyyy')}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); handleMonthChange(1); }} className="p-1 hover:bg-white/20 rounded transition-colors">
                    <ChevronRight/>
                    
                  </button>
               </div>
               
               <div className="p-4 bg-white">
                 <div className="grid grid-cols-7 mb-3 text-center text-sm font-bold text-gray-500 uppercase tracking-wide">
                   {weekDays.map(d => <div key={d}>{d}</div>)}
                 </div>
                 <div className="grid grid-cols-7 gap-1">
                    {days.map(day => {
                       const dateStr = format(day, 'yyyy-MM-dd');
                       const isSelected = value === dateStr;
                       const isTodayDate = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                       const isCurrentMonth = format(day, 'M') === format(viewDate, 'M');
                       const isDisabled = minDate && dateStr < minDate;
                       
                       return (
                         <button
                           key={day.toISOString()}
                           type="button"
                           onClick={(e) => { e.stopPropagation(); !isDisabled && handleSelect(day); }}
                           disabled={isDisabled}
                           className={`h-9 w-9 rounded-lg text-md font-semibold flex items-center justify-center transition-all mx-auto
                             ${!isCurrentMonth ? 'text-gray-300' : ''}
                             ${isDisabled ? 'text-gray-300 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-100'}
                             ${isSelected ? 'bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white shadow-md hover:from-[#2D869C] hover:to-[#3a9fb5]' : ''}
                             ${isTodayDate && !isSelected ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : ''}
                           `}
                         >
                           {format(day, 'd')}
                         </button>
                       );
                    })}
                 </div>
                 <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
                     <button
                        type="button"
                        onClick={() => { onChange(''); setIsOpen(false); }}
                        className="text-sm font-bold text-gray-500 hover:text-red-500 transition-colors uppercase tracking-wide"
                     >
                        Clear
                     </button>
                     <button
                        type="button"
                        onClick={() => { handleSelect(new Date()); }}
                        className="text-sm font-bold text-[#2D869C] hover:text-[#236b7a] transition-colors uppercase tracking-wide"
                     >
                        Today
                     </button>
                 </div>
               </div>
            </div>
         </>
       )}
    </div>
  );
};

const AvailabilityTab = ({ doctorId, selectedPatient }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'
  const [weekRange, setWeekRange] = useState(() => {
    const start = startOfWeek(new Date(), { weekStartsOn: 1 });
    const end = endOfWeek(start, { weekStartsOn: 1 });
    return { start, end };
  });

  // Helper function to get minimum time for time inputs (IST)
  const getMinTime = (isForEndTime = false) => {
    const isToday = formData.date === format(new Date(), 'yyyy-MM-dd');
    if (!isToday) {
      return isForEndTime && formData.startTime ? formData.startTime : undefined;
    }
    
    // Get current IST time
    const currentISTTime = getCurrentIST();
    const currentTime = format(currentISTTime, 'HH:mm');
    
    if (isForEndTime) {
      // For end time, use the later of current time or start time
      return formData.startTime && formData.startTime > currentTime 
        ? formData.startTime 
        : currentTime;
    }
    
    return currentTime;
  };

  // Early return if no doctorId
  if (!doctorId) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-pulse text-[#2D869C] mb-4">Loading doctor information...</div>
          <div className="text-sm text-[#838383]">Please wait while we load your profile</div>
        </div>
      </div>
    );
  }

  // Use the availability hook
  const {
    availabilityBlocks,
    weeklyView,
    loading,
    saving,
    error,
    loadDayAvailability,
    loadWeekAvailability,
    createAvailabilityBlock,
    updateAvailabilityBlock,
    deleteAvailabilityBlock,
    updatePreview,
    previewSlots
  } = useAvailability(doctorId);

  // Track last loaded week/range to avoid spamming API when sliding/selecting within same week
  const lastLoadedWeekKeyRef = useRef(null);

  // Create availability form state with IST-aware defaults
  const getInitialFormData = () => {
    const currentIST = getCurrentIST();
    const currentHour = currentIST.getHours();
    const currentMinutes = currentIST.getMinutes();
    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    
    let defaultStartTime = '09:00';
    let defaultEndTime = '21:00'; // Default to 9 PM
    
    if (isToday && currentHour >= 9) {
      // Round up to nearest quarter hour (00, 15, 30, 45)
      const quarterMinutes = Math.ceil(currentMinutes / 15) * 15;
      let startHour = currentHour;
      let startMinutes = quarterMinutes;
      
      if (quarterMinutes >= 60) {
        startHour = currentHour + 1;
        startMinutes = 0;
      }
      
      defaultStartTime = `${startHour.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;
      defaultEndTime = `${Math.min(startHour + 8, 23).toString().padStart(2, '0')}:00`;
    }
    
    return {
      date: format(selectedDate, 'yyyy-MM-dd'),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      slotMinutes: '30',
      numberOfSlots: '',
      bufferMinutes: 10, // Default to 10 minutes as per backend example
      maxParallel: 1,
      autoCalculate: true
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());

  const [formErrors, setFormErrors] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Normalize UI form data to payload-friendly types
  const normalizeFormData = (data) => ({
    ...data,
    slotMinutes: Number(data.slotMinutes) || 0,
    numberOfSlots: undefined,
    autoCalculate: true,
    bufferMinutes: Number(data.bufferMinutes) || 0,
    maxParallel: Number(data.maxParallel) || 1
  });

  const loadAvailability = useCallback(async (options = {}) => {
    if (!doctorId) return;
    const force = options.force === true;
    
    try {
      if (viewMode === 'day') {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        await loadDayAvailability(dateStr);
      } else {
        // Week view logic
        if (weekRange?.start && weekRange?.end) {
          const key = `${format(weekRange.start, 'yyyy-MM-dd')}|${format(weekRange.end, 'yyyy-MM-dd')}`;
          // Always reload if force is true, or if it's a new week range
          if (force || lastLoadedWeekKeyRef.current !== key) {
            // Reset the ref to force a fresh load
            if (force) {
              lastLoadedWeekKeyRef.current = null;
            }
            await loadWeekAvailability({ startDate: weekRange.start, endDate: weekRange.end });
            // Update the ref after successful load
            lastLoadedWeekKeyRef.current = key;
          }
        } else {
          const ws = startOfWeek(selectedDate, { weekStartsOn: 1 });
          const we = endOfWeek(ws, { weekStartsOn: 1 });
          const key = `${format(ws, 'yyyy-MM-dd')}|${format(we, 'yyyy-MM-dd')}`;
          // Always reload if force is true, or if it's a new week range
          if (force || lastLoadedWeekKeyRef.current !== key) {
            // Reset the ref to force a fresh load
            if (force) {
              lastLoadedWeekKeyRef.current = null;
            }
            await loadWeekAvailability(ws);
            // Update the ref after successful load
            lastLoadedWeekKeyRef.current = key;
          }
        }
      }
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  }, [doctorId, viewMode, selectedDate, weekRange, loadDayAvailability, loadWeekAvailability]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  // Handle date navigation
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    // Update form data date when date changes and refresh preview
    setFormData(prev => {
      const next = { ...prev, date: format(newDate, 'yyyy-MM-dd') };
      updatePreview(normalizeFormData(next));
      return next;
    });
  };

  const handleTodayClick = () => {
    const today = new Date();
    setSelectedDate(today);
    setFormData(prev => {
      const next = { ...prev, date: format(today, 'yyyy-MM-dd') };
      updatePreview(normalizeFormData(next));
      return next;
    });
  };

  const handlePreviousDay = () => {
    const prevDay = addDays(selectedDate, -1);
    setSelectedDate(prevDay);
    setFormData(prev => {
      const next = { ...prev, date: format(prevDay, 'yyyy-MM-dd') };
      updatePreview(normalizeFormData(next));
      return next;
    });
  };

  const handleNextDay = () => {
    const nextDay = addDays(selectedDate, 1);
    setSelectedDate(nextDay);
    setFormData(prev => {
      const next = { ...prev, date: format(nextDay, 'yyyy-MM-dd') };
      updatePreview(normalizeFormData(next));
      return next;
    });
  };

  const handleFormChange = (field, value) => {
    let newFormData = { ...formData, [field]: value };
    
    // Auto-adjust end time if start time changes and end time becomes invalid
    if (field === 'startTime' && newFormData.endTime && newFormData.endTime <= value) {
      // Add 1 hour to start time as default end time
      const startTime = new Date(`2000-01-01T${value}:00`);
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
      newFormData.endTime = format(endTime, 'HH:mm');
    }
    
    // Auto-adjust start time if date changes to today and current start time is in the past
    if (field === 'date') {
      const isNewDateToday = value === format(new Date(), 'yyyy-MM-dd');
      
      if (isNewDateToday) {
        // Get current IST time
        const currentISTTime = getCurrentIST();
        const currentHour = currentISTTime.getHours();
        const currentMinutes = currentISTTime.getMinutes();
        const currentTime = format(currentISTTime, 'HH:mm');
        
        if (newFormData.startTime < currentTime) {
          // Round up to nearest quarter hour (00, 15, 30, 45)
          const quarterMinutes = Math.ceil(currentMinutes / 15) * 15;
          let startHour = currentHour;
          let startMinutes = quarterMinutes;
          
          if (quarterMinutes >= 60) {
            startHour = currentHour + 1;
            startMinutes = 0;
          }
          
          newFormData.startTime = `${startHour.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;
          
          // Set end time to 8 hours after start, max 23:00
          const endHour = Math.min(startHour + 8, 23);
          newFormData.endTime = `${endHour.toString().padStart(2, '0')}:00`;
        } else if (newFormData.endTime <= currentTime) {
          // Just adjust end time if start time is valid but end time is not
          const startTime = new Date(`2000-01-01T${newFormData.startTime}:00`);
          const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
          newFormData.endTime = format(endTime, 'HH:mm');
        }
      }
    }
    
    setFormData(newFormData);
    
    // Clear specific field error
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Auto-generate preview if form is valid
    if (
      field === 'startTime' ||
      field === 'endTime' ||
      field === 'slotMinutes' ||
      field === 'bufferMinutes' ||
      field === 'numberOfSlots' ||
      field === 'autoCalculate' ||
      field === 'date'
    ) {
      updatePreview(normalizeFormData(newFormData));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.date) errors.date = 'Date is required';
    
    // Validate date is not in the past
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      errors.date = 'Cannot create availability for past dates';
    }
    
    if (!formData.startTime) errors.startTime = 'Start time is required';
    if (!formData.endTime) errors.endTime = 'End time is required';
    if (!formData.slotMinutes || Number(formData.slotMinutes) < 5) errors.slotMinutes = 'Slot duration must be at least 5 minutes';
    if (!formData.autoCalculate && (!formData.numberOfSlots || formData.numberOfSlots < 1)) {
      errors.numberOfSlots = 'Number of slots must be at least 1';
    }

    // Validate time logic
    const startTime = new Date(`2000-01-01T${formData.startTime}:00`);
    const endTime = new Date(`2000-01-01T${formData.endTime}:00`);
    
    if (endTime <= startTime) {
      errors.endTime = 'End time must be after start time';
    }

    // Check if selected date is today and times are in the past (using IST)
    if (formData.date === format(today, 'yyyy-MM-dd')) {
      // Get current IST time
      const currentISTTime = getCurrentIST();
      const currentTime = format(currentISTTime, 'HH:mm');
      
      if (formData.startTime && formData.startTime < currentTime) {
        errors.startTime = 'Start time cannot be in  past';
      }
      
      if (formData.endTime && formData.endTime < currentTime) {
        errors.endTime = 'End time cannot be in  past';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAvailability = async () => {
    if (!validateForm()) return;

    try {
      const payload = normalizeFormData(formData);

      const success = await createAvailabilityBlock(payload);

      if (success) {
        setShowCreateModal(false);
        resetFormData();
        // Small delay to ensure backend has processed the changes
        await new Promise(resolve => setTimeout(resolve, 300));
        // Clear the cache reference to force fresh data load
        lastLoadedWeekKeyRef.current = null;
        await loadAvailability({ force: true });
      } else {
        console.error('Failed to create availability block');
      }
    } catch (error) {
      console.error('Error creating availability:', error);
      
      // Show error popup for specific error messages
      if (error.message) {
        setErrorMessage(error.message);
        setShowErrorModal(true);
      }
    }
  };

  const handleEditBlock = (block) => {
    setSelectedBlock(block);
    const next = {
      date: block.date,
      startTime: block.startTime,
      endTime: block.endTime,
      slotMinutes: String(block.slotMinutes || '30'),
      numberOfSlots: '',
      bufferMinutes: block.bufferMinutes || 10, // Use existing buffer time or default to 10
      maxParallel: 1, // Default value since it's not in API response
      autoCalculate: true
    };
    setFormData(next);
    // Pre-generate preview for edit modal
    updatePreview(normalizeFormData(next));
    setShowEditModal(true);
  };

  const handleUpdateAvailability = async () => {
    if (!validateForm()) return;

    try {
      const payload = normalizeFormData(formData);

      const success = await updateAvailabilityBlock(selectedBlock._id || selectedBlock.id, payload);

      if (success) {
        setShowEditModal(false);
        setSelectedBlock(null);
        resetFormData();
        // Small delay to ensure backend has processed the changes
        await new Promise(resolve => setTimeout(resolve, 300));
        // Clear the cache reference to force fresh data load
        lastLoadedWeekKeyRef.current = null;
        await loadAvailability({ force: true });
      } else {
        console.error('Failed to update availability block');
      }
    } catch (error) {
      console.error('Error updating availability:', error);
      
      // Show error popup for specific error messages
      if (error.message) {
        setErrorMessage(error.message);
        setShowErrorModal(true);
      }
    }
  };

  const handleDeleteBlock = (block) => {
    setSelectedBlock(block);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const success = await deleteAvailabilityBlock(selectedBlock._id || selectedBlock.id, format(selectedDate, 'yyyy-MM-dd'));

      if (success) {
        setShowDeleteModal(false);
        setSelectedBlock(null);
        // Small delay to ensure backend has processed the changes
        await new Promise(resolve => setTimeout(resolve, 300));
        // Clear the cache reference to force fresh data load
        lastLoadedWeekKeyRef.current = null;
        await loadAvailability({ force: true });
      } else {
        console.error('Failed to delete availability block');
      }
    } catch (error) {
      console.error('Error deleting availability:', error);
    }
  };

  const resetFormData = () => {
    // Get current IST time to set reasonable defaults
    const currentIST = getCurrentIST();
    const currentHour = currentIST.getHours();
    const currentMinutes = currentIST.getMinutes();
    
    // Set default start time to current hour if today, otherwise 9 AM
    let defaultStartTime = '09:00';
    let defaultEndTime = '21:00'; // Default to 9 PM
    
    if (format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')) {
      // If it's today and current time is past 9 AM, use nearest quarter hour
      if (currentHour >= 9) {
        // Round up to nearest quarter hour (00, 15, 30, 45)
        const quarterMinutes = Math.ceil(currentMinutes / 15) * 15;
        let startHour = currentHour;
        let startMinutes = quarterMinutes;
        
        if (quarterMinutes >= 60) {
          startHour = currentHour + 1;
          startMinutes = 0;
        }
        
        defaultStartTime = `${startHour.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;
        defaultEndTime = `${Math.min(startHour + 8, 23).toString().padStart(2, '0')}:00`;
      }
    }
    
    setFormData({
      date: format(selectedDate, 'yyyy-MM-dd'),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      slotMinutes: '30',
      numberOfSlots: '',
      bufferMinutes: 10, // Default to 10 minutes as per backend example
      maxParallel: 1,
      autoCalculate: true
    });
  };

  // Ensure preview appears with defaults as soon as the create/edit modal opens
  useEffect(() => {
    if (showCreateModal) {
      updatePreview(normalizeFormData(formData));
    }
  }, [showCreateModal]);

  useEffect(() => {
    if (showEditModal && selectedBlock) {
      updatePreview(normalizeFormData(formData));
    }
  }, [showEditModal, selectedBlock]);

  return (
    <div className="space-y-6" style={{ fontFamily: 'Poppins' }}>
      {/* Header Section */}
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Availability Management</h2>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-white" />
                <span className="text-white font-semibold text-md">
                  {format(getCurrentIST(), 'h:mm a').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex justify-between gap-3">
            
            <button
              onClick={() => {
              // Reset form data with current IST time before opening modal
              const currentIST = getCurrentIST();
              const currentHour = currentIST.getHours();
              const currentMinutes = currentIST.getMinutes();
              const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
              
              let defaultStartTime = '09:00';
              let defaultEndTime = '21:00';
              
              if (isToday && currentHour >= 9) {
                // Round up to nearest quarter hour (00, 15, 30, 45)
                const quarterMinutes = Math.ceil(currentMinutes / 15) * 15;
                let startHour = currentHour;
                let startMinutes = quarterMinutes;
                
                if (quarterMinutes >= 60) {
                  startHour = currentHour + 1;
                  startMinutes = 0;
                }
                
                defaultStartTime = `${startHour.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;
                
                // Set end time to 8 hours after start, max 23:00
                const endHour = Math.min(startHour + 8, 23);
                defaultEndTime = `${endHour.toString().padStart(2, '0')}:00`;
              }
              
              setFormData({
                date: format(selectedDate, 'yyyy-MM-dd'),
                startTime: defaultStartTime,
                endTime: defaultEndTime,
                slotMinutes: '30',
                numberOfSlots: '',
                bufferMinutes: 10,
                maxParallel: 1,
                autoCalculate: true
              });
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-8 py-2.5 rounded-lg font-bold text-2xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <Plus className="w-7 h-7" />
            <span style={{ fontFamily: 'sans-serif' }}>Create Slots</span>
          </button>

          <div className="flex gap-5 ">
              <button
                onClick={() => setViewMode('day')}
                className={`px-4 py-2.5 text-md font-semibold transition-all ${
                  viewMode === 'day'
                    ? 'bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Day View
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-2.5 text-md font-semibold border-2  transition-all ${
                  viewMode === 'week'
                    ? 'bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Week View
              </button>
            </div>
        </div>
      </div>
      </div>

      {/* Date Navigation - Only for Day View */}
      {viewMode === 'day' && (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <input
                type="date"
                value={format(selectedDate, 'yyyy-MM-dd')}
                onChange={(e) => handleDateChange(new Date(e.target.value))}
                className="border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
              />
              <span className="text-gray-700 font-semibold whitespace-nowrap text-xl">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePreviousDay}
                className="p-2 rounded-lg transition-all text-gray-600 hover:text-[#2D869C] hover:bg-white border-2 border-transparent hover:border-gray-200"
              >
                <span className="text-xl">←</span>
              </button>
              <button
                onClick={handleTodayClick}
                disabled={format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')}
                className={`px-4 py-2 text-md font-semibold rounded-lg transition-all ${
                  format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                    ? 'bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white cursor-default'
                    : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-[#2D869C]'
                }`}
              >
                Today
              </button>
              <button
                onClick={handleNextDay}
                className="p-2 rounded-lg transition-all text-gray-600 hover:text-[#2D869C] hover:bg-white border-2 border-transparent hover:border-gray-200"
              >
                <span className="text-xl">→</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content based on view mode */}
      {viewMode === 'day' ? (
        <DayView 
          blocks={availabilityBlocks} 
          loading={loading} 
          selectedDate={selectedDate}
          onRefresh={loadAvailability}
          onEdit={handleEditBlock}
          onDelete={handleDeleteBlock}
        />
      ) : (
        <WeekView 
          weeklyData={weeklyView} 
          loading={loading} 
          selectedDate={selectedDate}
          weekRange={weekRange}
          onDateSelect={handleDateChange}
          onRangeApply={(start, end) => {
            // Normalize dates and apply range
            const startDate = new Date(start);
            const endDate = new Date(end);
            setWeekRange({ start: startDate, end: endDate });
            // Also move selected date to start for consistency
            setSelectedDate(startDate);
          }}
        />
      )}

      {/* Create Availability Modal */}
      {showCreateModal && (
        <CreateAvailabilityModal
          formData={formData}
          formErrors={formErrors}
          previewSlots={previewSlots}
          loading={saving}
          onFormChange={handleFormChange}
          onCreate={handleCreateAvailability}
          onClose={() => {
            setShowCreateModal(false);
            setFormErrors({});
          }}
          getMinTime={getMinTime}
        />
      )}

      {/* Edit Availability Modal */}
      {showEditModal && selectedBlock && (
        <CreateAvailabilityModal
          formData={formData}
          formErrors={formErrors}
          previewSlots={previewSlots}
          loading={saving}
          onFormChange={handleFormChange}
          onCreate={handleUpdateAvailability}
          onClose={() => {
            setShowEditModal(false);
            setSelectedBlock(null);
            setFormErrors({});
            resetFormData();
          }}
          title="Edit Availability Block"
          submitText="Update Block"
          getMinTime={getMinTime}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedBlock && (
        <DeleteConfirmationModal
          block={selectedBlock}
          loading={saving}
          onConfirm={handleConfirmDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedBlock(null);
          }}
        />
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <ErrorModal
          message={errorMessage}
          onClose={() => {
            setShowErrorModal(false);
            setErrorMessage('');
          }}
        />
      )}
    </div>
  );
};

// Day View Component
const DayView = ({ blocks, loading, selectedDate, onRefresh, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  
  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm p-8 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2D869C]"></div>
          <p className="text-gray-600 font-medium">Loading availability...</p>
        </div>
      </div>
    );
  }

  if (blocks.length === 0) {
    return (
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4">
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>No Availability Blocks</h3>
        </div>
        <div className="p-8 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium mb-2">No slots created for this day</p>
          <p className="text-sm text-gray-500 mb-4">Click "Create Slots" to add availability</p>
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white rounded-lg font-semibold transition-all"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {blocks.map((block) => {
        const blockId = block._id || block.id;
        return (
        <div key={blockId} className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-white" />
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>
                    {formatHHMMTo12Hour(block.startTime)} - {formatHHMMTo12Hour(block.endTime)}
                  </h3>
                  <p className="text-white/90 text-md mt-0.5">
                    {block.totalSlots || 0} slots • {block.slotMinutes} min each
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEdit && onEdit(block)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-md font-semibold transition-all flex items-center gap-1.5"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => onDelete && onDelete(block)}
                  disabled={block.bookedSlots > 0}
                  className="bg-red-500/20 hover:bg-red-500/30 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-md font-semibold transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
                <button
                  onClick={() => toggleExpanded(blockId)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-lg transition-all"
                >
                  <svg className={`w-5 h-5 transition-transform ${expanded[blockId] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

            {/* Block Details */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg p-3 border border-gray-200">
                <p className="text-md font-semibold text-gray-600 uppercase tracking-wide mb-1">Slot Duration</p>
                <p className="text-xl font-bold text-gray-800">{block.slotMinutes} min</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg p-3 border border-gray-200">
                <p className="text-md font-semibold text-gray-600 uppercase tracking-wide mb-1">Buffer Time</p>
                <p className="text-xl font-bold text-gray-800">{block.bufferMinutes || 0} min</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg p-3 border border-gray-200">
                <p className="text-md font-semibold text-gray-600 uppercase tracking-wide mb-1">Total Slots</p>
                <p className="text-xl font-bold text-gray-800">{block.totalSlots || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg p-3 border border-gray-200">
                <p className="text-md font-semibold text-gray-600 uppercase tracking-wide mb-1">Booked</p>
                <p className="text-xl font-bold text-gray-800">{block.bookedSlots || 0}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-lg text-gray-600 font-semibold mb-2">
                <span>Booking Progress</span>
                <span>
                  {block.totalSlots > 0 
                    ? Math.round((block.bookedSlots / block.totalSlots) * 100) 
                    : 0
                  }%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${block.totalSlots > 0 
                      ? (block.bookedSlots / block.totalSlots) * 100 
                      : 0
                    }%` 
                  }}
                ></div>
              </div>
            </div>

            {/* Slots Grid - Collapsible */}
            {expanded[blockId] && (
              <div className="mt-4 pt-4 border-t-2 border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xl font-semibold text-gray-700 uppercase tracking-wide">
                    Available Slots ({Array.isArray(block.slots) ? block.slots.length : 0})
                  </h4>
                  <div className="flex gap-3 text-md">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-gray-600">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>Open
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-semibold text-gray-600">
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>Booked
                    </span>
                  </div>
                </div>
                {Array.isArray(block.slots) && block.slots.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {block.slots.map((slot) => {
                      const start24 = formatUTCToISTTime(slot.startTime);
                      const end24 = formatUTCToISTTime(slot.endTime);
                      const start = formatHHMMTo12Hour(start24);
                      const end = formatHHMMTo12Hour(end24);
                      const isBooked = slot.status === 'Booked' || (slot.bookedAppointments && slot.bookedAppointments.length > 0);
                      return (
                        <div
                          key={slot._id}
                          className={`px-3 py-2 rounded-lg text-center text-md font-semibold border-2 transition-all ${
                            isBooked
                              ? 'bg-orange-50 border-orange-200 text-orange-700'
                              : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                          }`}
                          title={`${start} - ${end} IST (${slot.status})`}
                        >
                          <div>{start} - {end}</div>
                          {isBooked && (
                            <span className="block text-md text-orange-600 mt-0.5">Booked</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">No slots found for this block</div>
                )}
              </div>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
};

// Week View Component
const WeekView = ({ weeklyData, loading, selectedDate, weekRange, onDateSelect, onRangeApply }) => {
  const [localStart, setLocalStart] = useState(() => weekRange?.start || startOfWeek(selectedDate, { weekStartsOn: 1 }));
  const [localEnd, setLocalEnd] = useState(() => weekRange?.end || endOfWeek(localStart, { weekStartsOn: 1 }));

  useEffect(() => {
    // Sync local inputs when external range changes
    if (weekRange?.start) setLocalStart(new Date(weekRange.start));
    if (weekRange?.end) setLocalEnd(new Date(weekRange.end));
  }, [weekRange?.start, weekRange?.end]);

  useEffect(() => {
    // If no range provided, align local with selectedDate
    if (!weekRange?.start || !weekRange?.end) {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(start, { weekStartsOn: 1 });
      setLocalStart(start);
      setLocalEnd(end);
    }
  }, [selectedDate]);
  
  // Calendar state and slider state must be declared before any early return
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(localStart));
  const [showCalendar, setShowCalendar] = useState(false);

  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const weekStartsOn = 1; // Monday
  const leadBlanks = ((getDay(monthStart) + 6) % 7); // shift so Monday=0

  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm p-8 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2D869C]"></div>
          <p className="text-gray-600 font-medium">Loading week view...</p>
        </div>
      </div>
    );
  }

  // Determine if a given day is today for subtle highlight
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const selectedStr = format(selectedDate, 'yyyy-MM-dd');

  const selectedWeekStart = startOfWeek(localStart, { weekStartsOn: 1 });
  const selectedWeekEnd = endOfWeek(localStart, { weekStartsOn: 1 });

  const commitRange = (start) => {
    const s = new Date(start);
    const e = addDays(s, 6);
    setLocalStart(s);
    setLocalEnd(e);
    onRangeApply && onRangeApply(s, e);
  };

  const shiftByWeeks = (w) => {
    const base = startOfWeek(localStart, { weekStartsOn });
    const s = addWeeks(base, w);
    commitRange(s);
  };

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
      {/* Week Navigation Header */}
      <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 relative">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-white" />
            <span className="text-lg font-bold text-white" style={{ fontFamily: 'sans-serif' }}>
              {format(localStart, 'MMM d')} – {format(localEnd, 'MMM d, yyyy')}
            </span>
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex items-center gap-2 relative">
            <button
              type="button"
              onClick={() => setShowCalendar(!showCalendar)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all shadow-sm flex items-center gap-2 ${
                showCalendar 
                  ? 'bg-white text-[#2D869C] hover:bg-gray-100' 
                  : 'bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-2 border-white/30'
              }`}
            >
              <Calendar className="w-6 h-6" />
              {showCalendar ? 'Hide Calendar' : 'Select Dates'}
            </button>
            <button
              type="button"
              onClick={() => {
                const s = startOfWeek(new Date(), { weekStartsOn });
                commitRange(s);
                setCalendarMonth(startOfMonth(s));
              }}
              className="px-4 py-2 text-sm font-semibold bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg transition-all shadow-sm border-2 border-white/30"
            >
              This Week
            </button>
            
            {/* Calendar Dropdown */}
            {showCalendar && (
              <div className="absolute top-full right-0 mt-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="bg-white rounded-xl border-2 border-gray-200 shadow-xl w-[300px]">
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between px-4 py-3 border-b-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100/50">
                    <button
                      type="button"
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, -1))}
                      className="p-2 text-gray-600 hover:text-[#2D869C] hover:bg-white rounded-lg transition-all border-2 border-transparent hover:border-gray-200"
                      title="Previous month"
                    >
                      <span className="text-lg font-bold">‹‹</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCalendarMonth(addWeeks(calendarMonth, -1))}
                      className="p-2 text-gray-600 hover:text-[#2D869C] hover:bg-white rounded-lg transition-all border-2 border-transparent hover:border-gray-200"
                      title="Previous week"
                    >
                      <span className="text-lg font-bold">‹</span>
                    </button>
                    <div className="px-4 py-1 text-md font-bold text-gray-800" style={{ fontFamily: 'sans-serif' }}>
                      {format(calendarMonth, 'MMMM yyyy')}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCalendarMonth(addWeeks(calendarMonth, 1))}
                      className="p-2 text-gray-600 hover:text-[#2D869C] hover:bg-white rounded-lg transition-all border-2 border-transparent hover:border-gray-200"
                      title="Next week"
                    >
                      <span className="text-lg font-bold">›</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                      className="p-2 text-gray-600 hover:text-[#2D869C] hover:bg-white rounded-lg transition-all border-2 border-transparent hover:border-gray-200"
                      title="Next month"
                    >
                      <span className="text-lg font-bold">››</span>
                    </button>
                  </div>
                  
                  {/* Calendar Grid */}
                  <div className="p-4">
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Mo','Tu','We','Th','Fr','Sa','Su'].map((d, i) => (
                        <div key={i} className="text-center text-xs font-bold text-gray-600 py-1">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: leadBlanks }).map((_, i) => (
                        <div key={`b-${i}`} className="h-9" />
                      ))}
                      {daysInMonth.map((d) => {
                        const isInRange = d >= localStart && d <= localEnd;
                        const isToday = format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                        return (
                          <button
                            key={d.toISOString()}
                            onClick={() => {
                              commitRange(d);
                              setShowCalendar(false);
                            }}
                            className={`h-9 rounded-lg text-md font-semibold transition-all border-2 ${
                              isInRange
                                ? 'bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white shadow-sm border-[#2D869C]'
                                : isToday
                                ? 'bg-yellow-100 text-gray-700 border-yellow-400'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
                            }`}
                            title={`Select ${format(d, 'MMM d')}`}
                          >
                            {format(d, 'd')}
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Calendar Actions */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-gray-200">
                      <button
                        type="button"
                        onClick={() => setShowCalendar(false)}
                        className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const s = startOfWeek(new Date(), { weekStartsOn });
                          commitRange(s);
                          setCalendarMonth(startOfMonth(s));
                          setShowCalendar(false);
                        }}
                        className="px-3 py-1.5 text-sm bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white rounded-lg hover:from-[#236b7a] hover:to-[#2D869C] font-semibold transition-all"
                      >
                        Today
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Stats Bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100/50">
        <div className="flex gap-4 items-center">
          <span className="inline-flex items-center gap-2 text-base font-semibold text-gray-700">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>Open
          </span>
          <span className="inline-flex items-center gap-2 text-base font-semibold text-gray-700">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>Booked
          </span>
          <span className="inline-flex items-center gap-2 text-base font-semibold text-gray-700">
            <span className="w-3 h-3 rounded-full bg-gray-400"></span>None
          </span>
        </div>
        <div className="text-gray-700 font-semibold text-base">
          {(() => {
            const totals = weeklyData.reduce(
              (acc, d) => {
                acc.total += d.summary?.totalSlots || 0;
                acc.booked += d.summary?.bookedSlots || 0;
                acc.available += d.summary?.availableSlots || 0;
                return acc;
              },
              { total: 0, booked: 0, available: 0 }
            );
            return `Total: ${totals.total} • Booked: ${totals.booked} • Open: ${totals.available}`;
          })()}
        </div>
      </div>
      <div className="grid grid-cols-7 divide-x divide-gray-200 min-h-[600px]">
        {weeklyData.map((day) => {
          const dayStr = format(day.date, 'yyyy-MM-dd');
          const isSelected = dayStr === selectedStr;
          const isToday = dayStr === todayStr;
          const hasSlots = day.slots && day.slots.length > 0;
          return (
            <div
              key={day.dateStr}
              className={`p-3 flex flex-col transition-colors ${
                isSelected ? 'bg-[#9DC9D4]/20/50' : ''
              } ${isToday && !isSelected ? 'bg-yellow-50/40' : ''}`}
            >
              {/* Day header */}
              <div className="text-center mb-3">
                <div className="text-sm font-semibold tracking-wide uppercase text-[#838383]">
                  {day.dayName}
                </div>
                <button
                  onClick={() => onDateSelect && onDateSelect(day.date)}
                  className={`mt-1 w-9 h-9 rounded-full text-base font-semibold transition-colors border ${
                    isSelected
                      ? 'bg-[#2D869C] text-white border-[#2D869C] shadow-sm'
                      : 'text-[#838383] hover:bg-[#9DC9D4]/10 border-transparent'
                  }`}
                  title={`Select ${format(day.date, 'EEEE, MMM d')}`}
                >
                  {format(day.date, 'd')}
                </button>
                {/* Day summary */}
                <div className="mt-2 text-gray-500 leading-tight">
                  {day.summary?.totalSlots ? (
                    <>
                      <span className="font-semibold text-[#838383]">{day.summary.totalSlots}</span> slots
                      <span className="ml-1 text-green-600">{day.summary.availableSlots} open</span>
                      <span className="ml-1 text-orange-600">{day.summary.bookedSlots} booked</span>
                    </>
                  ) : (
                      <div className="text-md text-gray-600 text-center py-6 border border-dashed border-[#9DC9D4]/40 rounded">
                        No slots
                      </div>
                    )}
                </div>
              </div>
              {/* Slots list */}
              <div className="space-y-1 flex-1 overflow-y-auto custom-scrollbar pr-1">
                {hasSlots ? (
                  day.slots.map((slot) => {
                    const start = formatHHMMTo12Hour(slot.startTime);
                    const end = formatHHMMTo12Hour(slot.endTime);
                    const isBooked = slot.status === 'Booked';
                    return (
                      <div
                        key={slot.slotId}
                        className={`rounded border px-2 py-1.5 flex flex-col gap-0.5 shadow-sm ${
                          isBooked
                            ? 'bg-orange-50 border-orange-200 text-orange-800'
                            : 'bg-green-50 border-green-200 text-[#2D869C]'
                        }`}
                        title={`${start} - ${end} IST • ${isBooked ? 'Booked' : 'Open'}`}
                      >
                        <div className="font-semibold text-[15px] tracking-wide">
                          {start} - {end}
                        </div>
                        <div className="text-base uppercase font-bold opacity-80">
                          {isBooked ? 'BOOKED' : 'OPEN'}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-md text-gray-600 text-center py-6 border border-dashed border-[#9DC9D4]/40 rounded">
                    No slots
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Create Availability Modal Component
const CreateAvailabilityModal = ({ 
  formData, 
  formErrors, 
  previewSlots, 
  loading, 
  onFormChange, 
  onCreate, 
  onClose,
  getMinTime,
  title = "Create Availability Block",
  submitText = "Create"
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl  w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form Section */}
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-[#838383] flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#2D869C]" />
                Configuration
              </h4>

              {/* Date */}
              <div>
                <label className="block text-md font-semibold text-[#838383] mb-2">Date</label>
                <CustomDatePicker
                  value={formData.date}
                  onChange={(val) => onFormChange('date', val)}
                  minDate={format(new Date(), 'yyyy-MM-dd')}
                  error={formErrors.date}
                />
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-md font-semibold text-[#838383] mb-2">
                    Start Time (IST)
                  </label>
                  <TimeSelector
                    value={formData.startTime}
                    onChange={(value) => onFormChange('startTime', value)}
                    isToday={formData.date === format(new Date(), 'yyyy-MM-dd')}
                    minTime={getMinTime(false)}
                    error={formErrors.startTime}
                    autoFillCurrent={true}
                  />
                </div>
                <div>
                  <label className="block text-md font-semibold text-[#838383] mb-2">
                    End Time (IST)
                  </label>
                  <TimeSelector
                    value={formData.endTime}
                    onChange={(value) => onFormChange('endTime', value)}
                    isToday={formData.date === format(new Date(), 'yyyy-MM-dd')}
                    minTime={getMinTime(true)}
                    error={formErrors.endTime}
                  />
                </div>
              </div>

              {/* Slot Configuration */}
              <div>
                <label className="block text-md font-semibold text-[#838383] mb-2">
                  Slot Duration (minutes)
                </label>
                <select
                  value={formData.slotMinutes}
                  onChange={(e) => onFormChange('slotMinutes', e.target.value)}
                  className={`w-full border-2 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C] ${
                    formErrors.slotMinutes ? 'border-red-300' : 'border-gray-200'
                  }`}
                >
                  <option value="15">15 minutes</option>
                  <option value="20">20 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                </select>
                {formErrors.slotMinutes && <p className="text-[#E99797] text-sm mt-1">{formErrors.slotMinutes}</p>}
              </div>
              <p className="mt-2 text-sm font-medium text-gray-700">
                Auto-calculated no. of slots based on duration and buffer
              </p>

              {/* Buffer Time Configuration */}
              <div>
                <label className="block text-md font-semibold text-[#838383] mb-2">
                  Buffer Time (minutes)
                </label>
                <select
                  value={formData.bufferMinutes}
                  onChange={(e) => onFormChange('bufferMinutes', e.target.value)}
                  className="w-full border-2 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                >
                  <option className="text-md font-medium" value="0">No buffer time</option>
                  <option className="text-md font-medium" value="5">5 minutes</option>
                  <option className="text-md font-medium" value="10">10 minutes</option>
                  <option className="text-md font-medium" value="15">15 minutes</option>
                  <option className="text-md font-medium" value="20">20 minutes</option>
                  <option className="text-md font-medium" value="30">30 minutes</option>
                </select>
                
              </div>             
            </div>

            {/* Preview Section */}
            <div className="space-y-4 min-h-full">
              <h4 className="text-xl font-semibold text-[#838383] flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#2D869C]" />
                Slot Preview
              </h4>

              {previewSlots.length > 0 ? (
                <div className="bg-[#9DC9D4]/10 rounded-lg p-4 max-h-[430px] overflow-y-auto">
                  <div className="text-sm font-medium text-gray-700 mb-3">
                    {previewSlots.length} Slots will be created
                    {formData.bufferMinutes > 0 && (
                      <span className="ml-2 text-orange-600">
                        (with {formData.bufferMinutes} Min buffer)
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {previewSlots.map((slot, index) => (
                      <div key={slot.id} className="bg-white rounded p-3 border">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold uppercase">
                              Slot {index + 1}
                            </div>
                            <div className="text-md font-medium">
                                {formatHHMMTo12Hour(slot.startTime)} - {formatHHMMTo12Hour(slot.endTime)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-[#2D869C]">
                              {slot.duration} Min appointment
                            </div>
                            
                          </div>
                        </div>
                        {formData.bufferMinutes > 0 && index < previewSlots.length - 1 && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="text-sm text-orange-600 font-medium flex items-center gap-1">
                              <div className="w-2 h-2 bg-orange-300 rounded-full"></div>
                              {formData.bufferMinutes} Min buffer time before next slot
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-[#9DC9D4]/10 rounded-lg p-8 text-center">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Configure the settings to see slot preview</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100/50 flex justify-end gap-4 border-t-2 border-gray-200">
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all border-2 border-gray-300 text-sm shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            <X className="w-5 h-5" />
            <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>Cancel</span>
          </button>
          <button
            onClick={onCreate}
            disabled={loading || previewSlots.length === 0}
            className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>Creating...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6" />
                <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>{submitText} {previewSlots.length} Slots</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};



// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ block, loading, onConfirm, onClose }) => {
  const hasBookedSlots = block.bookedSlots > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl border-2 border-gray-200 w-full max-w-md shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Delete Availability Block</h3>
              <p className="text-white/90 text-sm mt-1">This action cannot be undone</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-5 h-5 text-[#2D869C]" />
              <span className="text-lg font-semibold text-gray-700\">
                {formatHHMMTo12Hour(block.startTime)} - {formatHHMMTo12Hour(block.endTime)}
              </span>
            </div>
            <div className="text-sm text-gray-600 space-y-1 ml-8 font-medium">
              <div>Date: {block.date}</div>
              <div>Total Slots: {block.totalSlots}</div>
              <div>Booked Appointments: {block.bookedSlots}</div>
            </div>
          </div>

          {hasBookedSlots ? (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="w-5 h-5" />
                <span className="font-bold">Cannot Delete Block</span>
              </div>
              <p className="text-red-700 text-sm mt-2 font-medium">
                This availability block has {block.bookedSlots} booked appointment{block.bookedSlots > 1 ? 's' : ''} and cannot be deleted. 
                Please cancel or reschedule the appointments first.
              </p>
            </div>
          ) : (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertCircle className="w-5 h-5" />
                <span className="font-bold">Confirm Deletion</span>
              </div>
              <p className="text-yellow-700 text-sm mt-2 font-medium">
                Are you sure you want to delete this availability block? This will remove all {block.availableSlots} available slot{block.availableSlots > 1 ? 's' : ''}.
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100/50 flex justify-end gap-4 border-t-2 border-gray-200">
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all border-2 border-gray-300 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            <X className="w-5 h-5" />
            <span className="text-lg font-bold\" style={{ fontFamily: 'sans-serif' }}>Cancel</span>
          </button>
          {!hasBookedSlots && (
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex items-center justify-center gap-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>Deleting...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-6 h-6" />
                  <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>Delete Block</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Error Modal Component
const ErrorModal = ({ message, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl  w-full max-w-md shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Error Creating Availability</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-gray-800 font-semibold text-lg">
                {message || 'An error occurred while creating the availability block'}
              </p>
            </div>
          </div>

          {/* Helpful suggestions based on common errors */}
          {message && message.toLowerCase().includes('overlap') && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mt-4">
              <div className="flex items-start gap-2">
                <div className="text-[#2D869C] mt-0.5">
                  <AlertCircle className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-[#2D869C] font-bold text-md">Suggestion:</h4>
                  <p className="text-gray-700 text-sm mt-1 font-medium">
                    The selected time range conflicts with an existing availability block. 
                    Please choose a different time or edit the existing block instead.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100/50 flex justify-end border-t-2 border-gray-200">
          <button
            onClick={onClose}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>Got it</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityTab;


